import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import axios from "axios";
import { SEARCH_LAB_BASE_URL, SEARCH_LAB_BASE_URL_INTERNAL, SEARCH_LAB_TOKEN } from "@globalConfig";
import { extractLangFromReq } from "@/common/axiosInstance";
import NotFound410Exception from "@/exceptions/NotFound410Exception";

/**
 * Dedicated HTTP client for the search-lab service (SE-12165).
 *
 * Differs from the main syarah_v1 fetcher:
 *  - Separate base URL (SEARCH_LAB_BASE_URL, the `/api/v1` root).
 *  - Bearer <SEARCH_LAB_TOKEN> auth instead of the syarah client token + JWT.
 *  - Locale is a path segment ("en" for English, omitted for Arabic default),
 *    NOT a base-URL segment like syarah_v1.
 *
 * Endpoints build their own path via {@link searchLabPath}.
 */

/**
 * Build a search-lab endpoint path with an explicit language segment.
 *
 * The lang segment comes FIRST, then the action directly (routes are flat —
 * no `search/` prefix anymore), e.g. ar/index, en/all-posts, ar/hot-deals.
 *
 * The lang segment is ALWAYS sent ("ar" or "en"). The route defines {lang?} as
 * optional, but omitting it on multi-segment paths makes Laravel bind the next
 * segment to {lang?}, fail the en|ar constraint and 404. Sending it explicitly
 * avoids that.
 *
 * @param group    legacy group arg, kept for call-site compatibility; ignored
 *                 because routes are now flat under {lang}.
 * @param endpoint action after the lang segment, e.g. "index" / "all-posts"
 * @param lang     "ar" | "en"
 */
export var searchLabPath = function searchLabPath(group, endpoint, lang) {
  var langSegment = lang === "en" ? "en" : "ar";
  var action = endpoint || "index";
  return "".concat(langSegment, "/").concat(action);
};
var buildBaseURL = function buildBaseURL(req) {
  return req ? SEARCH_LAB_BASE_URL_INTERNAL : SEARCH_LAB_BASE_URL;
};

/**
 * Create (or reuse) a search-lab axios instance for the given request context.
 * On SSR the instance is cached on `req` to avoid re-creating per call.
 */
export var createSearchLabFetcher = function createSearchLabFetcher() {
  var req = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
  if (req && req.__searchLabAxios) {
    return req.__searchLabAxios;
  }
  var instance = axios.create({
    baseURL: buildBaseURL(req),
    headers: _objectSpread(_objectSpread({}, SEARCH_LAB_TOKEN ? {
      Authorization: "Bearer ".concat(SEARCH_LAB_TOKEN)
    } : {}), {}, {
      "Content-Type": "application/json",
      Accept: "application/json"
    })
  });

  // A search-lab 400 means the request params are invalid (bad filter /
  // out-of-range value). Map it to NotFound410Exception — same as the other
  // APIs — so SSR renders the 404/410 page (via errorHandler → send404Page)
  // and CSR callers can navigate to it.
  instance.interceptors.response.use(function (response) {
    return response;
  }, function (error) {
    var _error$response;
    if ((error === null || error === void 0 ? void 0 : (_error$response = error.response) === null || _error$response === void 0 ? void 0 : _error$response.status) === 400) {
      var _error$config$url, _error$config;
      var url = (_error$config$url = error === null || error === void 0 ? void 0 : (_error$config = error.config) === null || _error$config === void 0 ? void 0 : _error$config.url) !== null && _error$config$url !== void 0 ? _error$config$url : "";
      return Promise.reject(new NotFound410Exception(url, "searchLab_400_invalid_params:" + url));
    }
    return Promise.reject(error);
  });
  if (req) {
    req.__searchLabAxios = instance;
  }
  return instance;
};
export var getSearchLabLang = function getSearchLabLang(req) {
  return extractLangFromReq(req);
};

/**
 * GET helper for every search-lab route (SE-12165 aligned the service on GET
 * only, matching the legacy syarah search APIs which were all GET too).
 *
 * Nested params (`filters`, `post_ids`) are serialized with axios' default
 * bracket notation (`filters[make_id][]=1`), which Laravel parses back into
 * the nested array the SearchRequest rules expect.
 */
export function searchLabGet(_x, _x2, _x3) {
  return _searchLabGet.apply(this, arguments);
}
function _searchLabGet() {
  _searchLabGet = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee(req, group, endpoint) {
    var params,
      config,
      lang,
      _args = arguments;
    return _regenerator().w(function (_context) {
      while (1) switch (_context.n) {
        case 0:
          params = _args.length > 3 && _args[3] !== undefined ? _args[3] : {};
          config = _args.length > 4 && _args[4] !== undefined ? _args[4] : {};
          lang = getSearchLabLang(req);
          return _context.a(2, createSearchLabFetcher(req).get(searchLabPath(group, endpoint, lang), _objectSpread(_objectSpread({}, config), {}, {
            params: params
          })));
      }
    }, _callee);
  }));
  return _searchLabGet.apply(this, arguments);
}